import { companyLocalToIso, isoToCompanyLocal, type ScheduleBlock } from "./dispatch";

export const TECHNICIAN_EVENT_KINDS = ["pto", "sick", "meeting", "out", "other", "training", "lunch"] as const;
export type TechnicianEventKind = (typeof TECHNICIAN_EVENT_KINDS)[number];

export const TECHNICIAN_EVENT_LABELS: Record<TechnicianEventKind, string> = {
  pto: "PTO",
  sick: "Sick",
  meeting: "Meeting",
  out: "Out",
  other: "Other",
  training: "Training",
  lunch: "Lunch",
};

export function isTechnicianEventKind(kind: string): kind is TechnicianEventKind {
  return (TECHNICIAN_EVENT_KINDS as readonly string[]).includes(kind);
}

export function technicianEventLabel(kind: string) {
  return isTechnicianEventKind(kind) ? TECHNICIAN_EVENT_LABELS[kind] : kind.replaceAll("_", " ");
}

export function defaultPaidForKind(kind: TechnicianEventKind) {
  return kind === "pto" || kind === "sick";
}

export function addCalendarDay(date: string, days = 1) {
  const value = new Date(`${date}T12:00:00.000Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function eventDateBounds(block: Pick<ScheduleBlock, "startsAt" | "endsAt" | "allDay">, timezone: string) {
  const start = isoToCompanyLocal(block.startsAt, timezone);
  const end = isoToCompanyLocal(block.endsAt, timezone);
  const startDate = start.slice(0, 10);
  const endDate = end.slice(0, 10);
  const allDay = Boolean(block.allDay) || (start.endsWith("T00:00") && end.endsWith("T00:00"));
  const inclusiveEnd = allDay && end.endsWith("T00:00") ? addCalendarDay(endDate, -1) : endDate;
  return {
    startDate,
    endDate: inclusiveEnd < startDate ? startDate : inclusiveEnd,
    startTime: start.slice(11),
    endTime: end.endsWith("T00:00") && allDay ? "17:00" : end.slice(11),
    allDay,
  };
}

export function eventRangeIso(input: {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  timezone: string;
}) {
  if (input.allDay) {
    return {
      startsAt: companyLocalToIso(`${input.startDate}T00:00`, input.timezone),
      endsAt: companyLocalToIso(`${addCalendarDay(input.endDate)}T00:00`, input.timezone),
      allDay: true,
    };
  }
  return {
    startsAt: companyLocalToIso(`${input.startDate}T${input.startTime}`, input.timezone),
    endsAt: companyLocalToIso(`${input.endDate}T${input.endTime}`, input.timezone),
    allDay: false,
  };
}

export function eventOverlapsDate(block: Pick<ScheduleBlock, "startsAt" | "endsAt">, date: string, timezone: string) {
  const start = isoToCompanyLocal(block.startsAt, timezone).slice(0, 10);
  const endLocal = isoToCompanyLocal(block.endsAt, timezone);
  const endDate = endLocal.endsWith("T00:00") ? addCalendarDay(endLocal.slice(0, 10), -1) : endLocal.slice(0, 10);
  return start <= date && date <= endDate;
}

export function eventLaneSpan(
  block: Pick<ScheduleBlock, "startsAt" | "endsAt">,
  date: string,
  timezone: string,
  startHour: number,
  endHour: number,
) {
  if (!eventOverlapsDate(block, date, timezone)) return null;
  const start = isoToCompanyLocal(block.startsAt, timezone);
  const end = isoToCompanyLocal(block.endsAt, timezone);
  const startDate = start.slice(0, 10);
  const endDate = end.slice(0, 10);
  const timeMinutes = (value: string) => {
    const [hour, minute] = value.split(":").map(Number);
    return hour * 60 + minute;
  };
  const visibleStart = startHour * 60;
  const visibleEnd = endHour * 60;
  const startMinutes = startDate < date ? visibleStart : Math.max(visibleStart, timeMinutes(start.slice(11)));
  const endsAtNextMidnight = endDate > date && end.endsWith("T00:00");
  const endMinutes = endDate > date || endsAtNextMidnight ? visibleEnd : Math.min(visibleEnd, timeMinutes(end.slice(11)));
  if (endMinutes <= startMinutes) return null;
  const span = visibleEnd - visibleStart;
  return {
    left: ((startMinutes - visibleStart) / span) * 100,
    width: ((endMinutes - startMinutes) / span) * 100,
  };
}
